import { type FC, useEffect } from 'react';
import { graphql, useRefetchableFragment, useRelayEnvironment } from 'react-relay';
import { isMobile } from 'dibs-media-query';
import { NavLayoutContainer } from './NavLayoutContainer';
import { useSendPresenceHeartbeats } from 'dibs-presence/exports/useSendPresenceHeartbeats';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy';
import { NavHeader } from './NavHeader';
import { NavLogoHamburger } from './NavLogoHamburger';
import { NavCreateListing } from './NavCreateListing';
import { NavItems } from './NavItems';
import { NavProfile } from './NavProfile';
import { NavSupportItems } from './NavSupportItems';
import { NavLogoutDownloadSellerApp } from './NavLogoutDownloadSellerApp';
import { useActiveNavLink } from './useActiveNavLink';
import { trackAbTestV2Variant } from 'dibs-ab-tests/exports/clientAbTestV2';
import { useNavContext } from '../../NavContext';
import { type NavRefetchQuery } from './__generated__/NavRefetchQuery.graphql';
import { type SellerNav_viewer$key } from './__generated__/SellerNav_viewer.graphql';

type Props = {
    viewer: SellerNav_viewer$key;
};

const viewerFragment = graphql`
    fragment SellerNav_viewer on Viewer
    @argumentDefinitions(renderNotifications: { type: "Boolean", defaultValue: false })
    @refetchable(queryName: "NavRefetchQuery") {
        ...NavHeader_viewer
        ...useSendPresenceHeartbeats_viewer
        ...NavItems_viewer
        ...NavProfile_viewer
        ...NavLogoHamburger_viewer
        seller(sellerId: $sellerPk) @include(if: $hasSellerPk) {
            serviceId
            ...NavCreateListing_seller
            ...NavItems_seller
            ...NavProfile_seller
            ...useSendPresenceHeartbeats_seller
            ...NavLogoHamburger_seller
        }
        dealerNavBar(
            sellerPk: $sellerPk
            dotComHost: $dotComHost
            hyperwalletLink: $hyperwalletLink
            customBannerText: $customBannerText
            userId: $userId
        ) {
            buttons {
                ...NavCreateListing_buttons
            }
            links {
                ...NavHeader_links
                ...NavItems_links
                navItems {
                    ...useActiveNavLink_navItems
                }
            }
            notifications: badges @include(if: $renderNotifications) {
                ...NavItems_notifications
                ...NavLogoHamburger_notifications
            }
        }
    }
`;

export const SellerNav: FC<Props> = ({ viewer: viewerRef }) => {
    const [viewer, refetch] = useRefetchableFragment<NavRefetchQuery, SellerNav_viewer$key>(
        viewerFragment,
        viewerRef
    );
    const environment = useRelayEnvironment();
    const { status } = useNavContext();

    useEffect(() => {
        trackAbTestV2Variant('seller-aa-test-3');
        trackAbTestV2Variant('seller-aa-test-4');
        trackAbTestV2Variant('seller-aa-test-5');
    }, []);

    const seller = viewer?.seller;

    useSendPresenceHeartbeats({
        environment,
        viewer,
        seller,
        isInternalUser: false,
    });

    const { serviceId: hasSellerPk } = seller || {};

    useEffect(() => {
        if (hasSellerPk) {
            refetch({ renderNotifications: true });
        }
    }, [refetch, hasSellerPk]);

    const dealerNavBar = viewer?.dealerNavBar;
    const buttons = dealerNavBar?.buttons;
    const links = dealerNavBar?.links;
    const notifications = dealerNavBar?.notifications;
    const navItems = dealerNavBar?.links?.navItems?.filter(filterFalsy);

    const activeLink = useActiveNavLink({
        navItems,
    });

    if (!seller || !dealerNavBar || !buttons || !links) {
        return null;
    }

    const truthyButtons = buttons.filter(filterFalsy);
    const isMobileView = isMobile();

    return (
        <nav data-tn="top-navbar" id="js-header">
            <>
                {!isMobileView && (
                    <NavHeader viewer={viewer} links={links} activeLink={activeLink} />
                )}
                <NavLayoutContainer>
                    <NavLogoHamburger
                        viewer={viewer}
                        seller={seller}
                        notifications={notifications}
                    />
                    <NavCreateListing seller={seller} buttons={truthyButtons} />
                    <NavItems
                        seller={seller}
                        links={links}
                        notifications={notifications}
                        activeLink={activeLink}
                        viewer={viewer}
                    />
                    {isMobileView && status === 'overlay' ? (
                        <>
                            <NavSupportItems />
                            <NavLogoutDownloadSellerApp />
                        </>
                    ) : (
                        <NavProfile viewer={viewer} seller={seller} />
                    )}
                </NavLayoutContainer>
            </>
            <div id="nav-bottom-marker" />
        </nav>
    );
};
